import { memo, useCallback, useEffect, useMemo, useState } from "react"
import { Text, VStack } from "@chakra-ui/react"
import Dialog from "../chakra/Dialog"
import { ActionButton } from "../content/components/Button"
import { toaster } from "../chakra/Toaster"
import { CONNECTOR_CONFIG, ConnectorTypeKey, getConnectorConfig } from "./constants"
import { matchesAssetClass } from "./sourceFilterUtils"
import { AvailableConnection, UnlinkedInstance } from "./types"
import SuggestionListInput from "../components/SuggestionListInput"

export interface AddConnectionSubmitPayload {
  monitorId: string
  externalId: string | null
  monitoringInstanceId?: string
  createNew?: boolean
  /** Device model key for connectors with nested template registries (e.g. TYCROP → "zwg"). */
  deviceModel?: string
}

export interface SharedAddConnectionDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (payload: AddConnectionSubmitPayload) => Promise<void>
  connectors: AvailableConnection[]
  loadingConnectors?: boolean
  /** Unlinked instances for the selected connector. */
  unlinkedInstances?: UnlinkedInstance[]
  loadingUnlinked?: boolean
  /** Dynamic hook or callback called when monitorId changes to fetch unlinked instances. */
  onConnectorChange?: (monitorId: string) => void
  creating?: boolean
  /** Filter connectors by asset class. If provided (or showConnectorTypeSelect is false), single-stage select is used. */
  assetClass?: string | null | undefined
  /** Whether to show explicit two-stage Connector Type -> Connector selects (defaults to false if assetClass is passed). */
  showConnectorTypeSelect?: boolean
  title?: string
}

interface SelectFieldProps {
  label: string
  value: string
  items: Array<{ value: string; label: string }>
  disabled: boolean
  onChange: (value: string) => void
  helperText?: string
}

const SelectField = memo(function SelectField({
  label,
  value,
  items,
  disabled,
  onChange,
  helperText,
}: SelectFieldProps) {
  const filteredItems = useMemo(() => items.filter((i) => i.value !== ""), [items])
  const placeholderItem = items.find((i) => i.value === "")

  return (
    <SuggestionListInput
      label={label}
      value={value}
      onChange={onChange}
      items={filteredItems}
      disabled={disabled}
      placeholder={placeholderItem?.label || `Select ${label.toLowerCase()}...`}
      helperText={helperText}
    />
  )
})

export function SharedAddConnectionDialog({
  isOpen,
  onClose,
  onSubmit,
  connectors,
  loadingConnectors = false,
  unlinkedInstances = [],
  loadingUnlinked = false,
  onConnectorChange,
  creating = false,
  assetClass,
  showConnectorTypeSelect,
  title = "Add data connection",
}: SharedAddConnectionDialogProps) {
  const isTwoStage = showConnectorTypeSelect ?? (assetClass === undefined && connectors.some(c => c.connectorType))

  const [selectedConnectorType, setSelectedConnectorType] = useState("")
  const [monitorId, setMonitorId] = useState("")
  const [externalIdValue, setExternalIdValue] = useState("")
  const [error, setError] = useState<string | null>(null)

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedConnectorType("")
      setMonitorId("")
      setExternalIdValue("")
      setError(null)
    }
  }, [isOpen])

  // Filter connectors by asset class (if in single stage mode or assetClass supplied)
  const compatibleConnectors = useMemo(() => {
    if (assetClass === undefined) return connectors
    return connectors.filter((c) => matchesAssetClass(c.compatibleClasses, assetClass))
  }, [connectors, assetClass])

  // Derive available connector types for two-stage selection
  const availableTypes = useMemo(() => {
    const list =
      assetClass !== undefined
        ? connectors.filter((c) => matchesAssetClass(c.compatibleClasses, assetClass))
        : connectors
    const types = [...new Set(list.map((c) => c.connectorType))]
    return types
      .filter((t): t is ConnectorTypeKey => t in CONNECTOR_CONFIG)
      .map((t) => ({ value: t, label: CONNECTOR_CONFIG[t].displayName }))
  }, [connectors, assetClass])

  // Filter connectors by chosen type in two-stage mode
  const connectorsForSelectedType = useMemo(() => {
    if (!selectedConnectorType) return []
    let list = connectors.filter((c) => c.connectorType === selectedConnectorType)
    if (assetClass !== undefined) {
      list = list.filter((c) => matchesAssetClass(c.compatibleClasses, assetClass))
    }
    return list
  }, [connectors, selectedConnectorType, assetClass])

  // Handle connector selection change
  const handleConnectorChange = useCallback(
    (value: string) => {
      setMonitorId(value)
      setExternalIdValue("")
      setError(null)
      onConnectorChange?.(value)
    },
    [onConnectorChange],
  )

  const handleConnectorTypeChange = useCallback(
    (value: string) => {
      setSelectedConnectorType(value)
      setMonitorId("")
      setExternalIdValue("")
      setError(null)
      onConnectorChange?.("")
    },
    [onConnectorChange],
  )

  // Auto-select first connector if single-stage and only 1 compatible connector exists
  useEffect(() => {
    if (!isTwoStage && !loadingConnectors && compatibleConnectors.length === 1 && !monitorId) {
      handleConnectorChange(compatibleConnectors[0].monitorId)
    }
  }, [isTwoStage, loadingConnectors, compatibleConnectors, monitorId, handleConnectorChange])

  // Auto-select first connector type if two-stage and only 1 connector type available
  useEffect(() => {
    if (isTwoStage && !loadingConnectors && availableTypes.length === 1 && !selectedConnectorType) {
      handleConnectorTypeChange(availableTypes[0].value)
    }
  }, [isTwoStage, loadingConnectors, availableTypes, selectedConnectorType, handleConnectorTypeChange])

  // Auto-select single connector in two-stage if only 1 connector exists for selected type
  useEffect(() => {
    if (isTwoStage && selectedConnectorType && connectorsForSelectedType.length === 1 && !monitorId) {
      handleConnectorChange(connectorsForSelectedType[0].monitorId)
    }
  }, [isTwoStage, selectedConnectorType, connectorsForSelectedType, monitorId, handleConnectorChange])

  // Chosen connector details
  const chosenConnector = useMemo(
    () => connectors.find((c) => c.monitorId === monitorId),
    [connectors, monitorId],
  )

  const connectorType = chosenConnector?.connectorType ?? selectedConnectorType ?? "VRM"
  const config = getConnectorConfig(connectorType)

  // Single-stage select items
  const singleStageItems = useMemo(() => {
    const hasClassFilter = assetClass !== undefined && assetClass !== null
    let defaultLabel = "Select a connector for this asset..."
    if (loadingConnectors) {
      defaultLabel = "Loading..."
    } else if (compatibleConnectors.length === 0) {
      defaultLabel = hasClassFilter
        ? "No connectors can monitor this asset class"
        : "No connectors available"
    }

    return [
      { value: "", label: defaultLabel },
      ...compatibleConnectors.map((c) => ({
        value: c.monitorId,
        label: c.monitorName ?? c.monitorId,
      })),
    ]
  }, [compatibleConnectors, loadingConnectors, assetClass])

  // Two-stage connector type items
  const connectorTypeItems = useMemo(
    () => [
      {
        value: "",
        label: loadingConnectors
          ? "Loading..."
          : availableTypes.length === 0
            ? "No connector types available"
            : "Select a connector type...",
      },
      ...availableTypes,
    ],
    [availableTypes, loadingConnectors],
  )

  // Two-stage connector items
  const twoStageConnectorItems = useMemo(
    () => [
      {
        value: "",
        label: loadingConnectors
          ? "Loading..."
          : !selectedConnectorType
            ? "Select a connector type first..."
            : connectorsForSelectedType.length === 0
              ? "No connectors available"
              : "Select a connector for this connection...",
      },
      ...connectorsForSelectedType.map((c) => ({
        value: c.monitorId,
        label: c.monitorName ?? c.monitorId,
      })),
    ],
    [connectorsForSelectedType, loadingConnectors, selectedConnectorType],
  )

  const comboboxItems = useMemo(() => {
    const seen = new Set<string>()
    const items: Array<{ value: string; label: string }> = []
    for (const inst of unlinkedInstances) {
      const val = inst.externalId ?? inst.monitoringInstanceId
      if (val && !seen.has(val)) {
        seen.add(val)
        items.push({ value: val, label: val })
      }
    }
    return items
  }, [unlinkedInstances])

  const trimmedExternalId = externalIdValue.trim()
  const canSubmit =
    Boolean(monitorId) &&
    (!config.requiresExternalId || (trimmedExternalId.length > 0 && !trimmedExternalId.includes(" ")))

  const handleSave = async () => {
    if (!canSubmit) return

    const matchingInstance = unlinkedInstances.find(
      (i) => i.externalId === trimmedExternalId || i.monitoringInstanceId === trimmedExternalId,
    )

    try {
      setError(null)
      await onSubmit({
        monitorId,
        externalId: config.requiresExternalId ? (matchingInstance?.externalId ?? trimmedExternalId) : null,
        monitoringInstanceId: config.requiresExternalId ? (matchingInstance?.monitoringInstanceId || undefined) : undefined,
        createNew: config.requiresExternalId ? !matchingInstance : undefined,
        deviceModel: config.defaultDeviceModel,
      })
      onClose()
    } catch (err: any) {
      const message = err?.message || "Failed to create data connection"
      setError(message)
    }
  }

  return (
    <Dialog
      title={title}
      open={isOpen}
      setOpen={(openState) => {
        if (!openState) onClose()
      }}
      onClose={onClose}
      bodyOverflow="visible"
      footer={
        <>
          <ActionButton
            disabled={creating}
            context={{ label: "Cancel", description: "Cancel adding connection", variant: "secondary" }}
            onClick={onClose}
          />
          <ActionButton
            disabled={!canSubmit || creating}
            context={{ label: "Create", description: "Create the data connection", variant: "primary" }}
            onClick={handleSave}
          />
        </>
      }
    >
      <VStack gap={4} width="100%" align="stretch">
        {isTwoStage ? (
          <>
            <SelectField
              label="Connector type"
              value={selectedConnectorType}
              items={connectorTypeItems}
              disabled={loadingConnectors || availableTypes.length === 0}
              onChange={handleConnectorTypeChange}
            />
            <SelectField
              label="Connector"
              value={monitorId}
              items={twoStageConnectorItems}
              disabled={loadingConnectors || creating || !selectedConnectorType}
              onChange={handleConnectorChange}
              helperText="Select a connector for this connection"
            />
          </>
        ) : (
          <SelectField
            label="Connector"
            value={monitorId}
            items={singleStageItems}
            disabled={loadingConnectors || creating || compatibleConnectors.length === 0}
            onChange={handleConnectorChange}
          />
        )}

        {monitorId && config.requiresExternalId && (
          <SuggestionListInput
            label={config.externalIdLabel}
            value={externalIdValue}
            onChange={setExternalIdValue}
            items={comboboxItems}
            disabled={creating || loadingUnlinked}
            placeholder={config.externalIdPlaceholder || `Select or type ${config.externalIdLabel.toLowerCase()}...`}
            required
            invalid={externalIdValue.includes(" ")}
            errorText="Spaces are not allowed"
            helperText={`Select an existing ${config.externalIdLabel.toLowerCase()} or type a new one.`}
          />
        )}

        {error && (
          <Text color="red.500" fontSize="sm">
            {error}
          </Text>
        )}
      </VStack>
    </Dialog>
  )
}

export default SharedAddConnectionDialog
